import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as LanguageIcon,

  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { agencyApi } from '../services/api';

const steps = ['Basic Info', 'Branding', 'Domain'];

const CreateSubaccount = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    brandName: '',
    primaryColor: '#06AB8B',
    secondaryColor: '#049572',
    customDomain: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');

    // Auto-generate slug from name
    if (name === 'name' && !formData.slug) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0) {
      if (!formData.name.trim()) {
        setError('Subaccount name is required');
        return;
      }
      if (!formData.slug.trim()) {
        setError('Slug is required');
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await agencyApi.createSubaccount(formData);

      if (response.data.success) {
        toast.success('Subaccount created successfully!');
        navigate('/subaccounts');
      } else {
        setError(response.data.message || 'Failed to create subaccount');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: '#1a1a2e', mb: 3 }}
            >
              Basic Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subaccount Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., ABC Healthcare"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL Slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  placeholder="abc-healthcare"
                  helperText="This will be used in the subdomain URL"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Brand Name (optional)"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleChange}
                  placeholder="Display name for branding"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: '#1a1a2e', mb: 3 }}
            >
              Branding Colors
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Primary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="color"
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    style={{
                      width: 60,
                      height: 40,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  />
                  <TextField
                    size="small"
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Secondary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="color"
                    name="secondaryColor"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    style={{
                      width: 60,
                      height: 40,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  />
                  <TextField
                    size="small"
                    name="secondaryColor"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Preview */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Preview
              </Typography>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${formData.primaryColor} 0%, ${formData.secondaryColor} 100%)`,
                }}
              >
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {formData.name || 'Your Brand'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Sample branding preview
                </Typography>
              </Paper>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: '#1a1a2e', mb: 3 }}
            >
              Domain Settings
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Your subaccount will be available at:
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#f5f7fa',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontFamily: 'monospace', color: '#06AB8B' }}
                >
                  https://{formData.slug || 'your-slug'}.yourdomain.com
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Custom Domain (Optional)
            </Typography>
            <TextField
              fullWidth
              name="customDomain"
              value={formData.customDomain}
              onChange={handleChange}
              placeholder="intake.clientdomain.com"
              helperText="Enter a custom domain for this subaccount. DNS configuration will be required."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />

            {formData.customDomain && (
              <Alert severity="info" sx={{ mt: 2 }}>
                After creating the subaccount, you'll need to add a CNAME record
                pointing to your main domain.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subaccounts')}
          sx={{ mb: 2, color: '#666' }}
        >
          Back to Subaccounts
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          Create New Subaccount
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          Set up a new branded subaccount for your client
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          mb: 3,
        }}
      >
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent()}

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 4,
            pt: 3,
            borderTop: '1px solid #eee',
          }}
        >
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
            endIcon={
              activeStep === steps.length - 1 ? (
                <CheckIcon />
              ) : (
                <ArrowForwardIcon />
              )
            }
          >
            {activeStep === steps.length - 1
              ? loading
                ? 'Creating...'
                : 'Create Subaccount'
              : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateSubaccount;
